
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import BarcodeReaderMixin from '@/components/mixins/BarcodeReaderMixin.vue'
import ScanArea from '@/components/svg/ScanArea.vue'
import errorHandler from '@/utils/errorHandler'
import { promptVideoPermission } from '@/utils/helpers'
import { TError } from 'smartbarcode-web-core/src/utils/types/index'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { Options } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import { EBCRType } from 'smartbarcode-web-core/src/utils/enums/mobileEnums'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'

@Options({
  components: {
    ScanArea,
    BarcodeSymbologiesSelection,
    SelectionCameraPopup,
    IconSwitch,
  },
  emits: ['validate:locationId'],
  name: 'LocationScan',
})
export default class LocationScan extends BarcodeReaderMixin {
  @Prop({ type: String }) readonly operationType?: string

  _barcodeDataCapture: BarcodeCapture | undefined
  codeReader = new BrowserMultiFormatReader()
  isReadyToScan = false
  video: HTMLVideoElement | null = null

  defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.isReadyToScan = true
    this.codeReader = codeReader
    if (!result) return

    const temp: { [key: string]: unknown } = { ...result }
    this.handleScanResult((temp.text as string) || '')
    // codeReader.reset()
  }

  async scanditDataResult(symbology: Barcode) {
    this.isReadyToScan = true
    if (!symbology) return

    this.handleScanResult(symbology.data || '')
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    this.isReadyToScan = true
    if (result.length < 1) return
    const firstDetectedBarcode = result[0]?.rawValue || ''
    this.handleScanResult(firstDetectedBarcode)
  }

  unmounted() {
    this.video = document.getElementById('video') as HTMLVideoElement
    if (this.video?.srcObject) {
      ;(this.video.srcObject as MediaStream).getTracks().forEach((track) => {
        if (track.readyState === 'live') {
          track.enabled = false
          track.stop()
        }
      })
      if (this.codeReader) this.codeReader.reset()
    }
  }

  async setupCameraSetting() {
    try {
      this.isReadyToScan = false
      this.$nextTick(async () => {
        switch (this.barcodeReaderType as EBCRType) {
          case EBCRType.SCANDIT:
            await this.initScanditReader()
            break
          case EBCRType.BARCODE_DETECTION:
            await this.initBarcodeReaderPolyFill()
            break
          default:
            await promptVideoPermission()
            await this.initZxingReader()
            break
        }
      })
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.isReadyToScan = true
    }
  }

  async mounted() {
    await this.initCameraSelection()
    this.setupCameraSetting()
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            width: { ideal: 1000 },
            height: { ideal: 1000 },
            frameRate: {
              ideal: 30,
              max: 60,
            },
            aspectRatio: 1,
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          console.log('error', e)
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            aspectRatio: 1,
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
    }
  }

  handleScanResult(text: string) {
    this.$emit('validate:locationId', text)
  }
}
